import { z } from 'zod'

import { EmptyArraySchema } from 'shared/api/common/schemas/base'
import { AdaptedMapSchema } from 'shared/api/common/schemas/templates/adaptedMap'
import { DescriptionSchema } from 'shared/api/common/schemas/templates/description'
import { MapSchema } from 'shared/api/common/schemas/templates/map'
import { SelectionSchema } from 'shared/api/common/schemas/templates/selection'

const MappingItemSchema = z
  .record(z.string().or(z.number()), z.number().nullable())
  .or(EmptyArraySchema)
  .optional()

const TemplateFieldSchema = z.object({
  wizard_enabled: z.boolean().optional(),
  is_template_field: z.boolean().optional(),
  value: z.string().optional(),
  template_field_id: z.number(),
})

const TemplateFieldAdaptedSchema = TemplateFieldSchema.extend({
  field_id: z.number(),
})

export const InstalledTemplateSchema = z.object({
  installation_id: z.number(),
  template_id: z.number(),
  version_id: z.number(),
  title: z.string(),
  description: DescriptionSchema.nullable(),
  template_url: z.string().nullable(),
  template_avatar: z.object({
    avatar_url_100x100: z.string().nullable(),
    color: z.string(),
  }),
  details: z.object({
    installation_settings: z.object({
      replace_settings: z.object({
        main_menu: z.boolean(),
        welcome_message: z.boolean(),
        story_mention_reply: z.boolean().optional(),
        default_reply: z.boolean(),
        default_reply_instagram: z.boolean().optional(),
        ice_breakers: z.boolean(),
        ice_breakers_instagram: z.boolean().optional(),
        ice_breakers_whatsapp: z.boolean().optional(),
        telegram_default_reply: z.boolean().optional(),
        telegram_welcome_message: z.boolean().optional(),
        whatsapp_welcome_message: z.boolean().optional(),
        instagram_main_menu: z.boolean().optional(),
      }),
      mapping_settings: z.object({
        custom_fields: MappingItemSchema,
        tags: MappingItemSchema,
        global_fields: MappingItemSchema,
        otn_reasons: MappingItemSchema,
        custom_events: MappingItemSchema,
      }),
      template_fields_installation_map: z
        .record(z.string().or(z.number()), z.number())
        .or(EmptyArraySchema),
    }),
    template_root_folder_id: z.number().nullable(),
    template_cufs_folder_id: z.number().nullish(),
    template_gafs_folder_id: z.number().nullish(),
    template_tags_folder_id: z.number().nullish(),
  }),
  author_name: z.string(),
  template_author_user_id: z.number(),
  icon: z.string().nullable(),
  is_protected: z.boolean(),
  template_installation_access_data: z.object({
    has_user_access: z.boolean(),
    is_locked: z.boolean(),
    is_protected: z.boolean(),
    is_disabled: z.boolean(),
    is_owner: z.boolean(),
  }),
  template_fields: z.array(TemplateFieldSchema).nullish(),
  map: MapSchema.optional(),
})

export const AdaptedInstalledTemplateSchema = z.object({
  installationId: z.number(),
  templateId: z.number(),
  templateUrl: z.string().nullable(),
  details: z.object({
    installation_settings: z.object({
      replace_settings: z.object({
        main_menu: z.boolean(),
        welcome_message: z.boolean(),
        story_mention_reply: z.boolean().optional(),
        default_reply: z.boolean(),
        default_reply_instagram: z.boolean().optional(),
        ice_breakers: z.boolean(),
        ice_breakers_instagram: z.boolean().optional(),
        ice_breakers_whatsapp: z.boolean().optional(),
        telegram_default_reply: z.boolean().optional(),
        telegram_welcome_message: z.boolean().optional(),
        whatsapp_welcome_message: z.boolean().optional(),
        instagram_main_menu: z.boolean().optional(),
      }),
      mapping_settings: z.object({
        custom_fields: MappingItemSchema,
        tags: MappingItemSchema,
        global_fields: MappingItemSchema,
        otn_reasons: MappingItemSchema,
        custom_events: MappingItemSchema,
      }),
      template_fields_installation_map: z
        .record(z.string().or(z.number()), z.number())
        .or(EmptyArraySchema),
    }),
    template_root_folder_id: z.number().nullable(),
    template_cufs_folder_id: z.number().nullish(),
    template_gafs_folder_id: z.number().nullish(),
    template_tags_folder_id: z.number().nullish(),
  }),
  versionId: z.number(),
  title: z.string(),
  description: DescriptionSchema.nullable(),
  author: z.string(),
  id: z.number(),
  avatar: z.object({
    url: z.string().nullable(),
    color: z.string(),
  }),
  authorUserdId: z.number(),
  template_installation_access_data: z.object({
    has_user_access: z.boolean(),
    is_locked: z.boolean(),
    is_protected: z.boolean(),
    is_disabled: z.boolean(),
    is_owner: z.boolean(),
  }),
  templateFields: z.array(TemplateFieldAdaptedSchema),
  showWizard: z.boolean(),
  selection: SelectionSchema.optional(),
  map: AdaptedMapSchema.optional(),
})

export type InstalledTemplate = z.infer<typeof InstalledTemplateSchema>
export type AdaptedInstalledTemplate = z.infer<typeof AdaptedInstalledTemplateSchema>
export type TemplateFieldAdapted = z.infer<typeof TemplateFieldAdaptedSchema>
